// ─────────────────────────────────────────────────────────────────────────────
// Slides del Recap por OU (war room 2026-10-20). Antes, apagar slides con «México» elegido los apagaba
// para TODAS las OUs: el encendido/apagado era uno por edición. Ahora cada OU puede tener su propio juego
// de slides encendidos (`RecapEditionDto.slidesPorOu`); sin personalización, usa la configuración Global
// (el `activo` de cada slide).
//
// Misma regla que el servidor (`RecapSlidesPorOu`, SurveyService): la personalización de la OU más
// cercana en la cadena hoja → raíz gana, así que la de EUROPA llega a sus sub-OUs y la de una sub-OU
// manda sobre la de su padre. Aquí la cadena sale del catálogo de OUs (`parentOuId`), para que la
// pantalla diga de quién hereda cada OU y la vista previa muestre lo que verá esa OU.
// ─────────────────────────────────────────────────────────────────────────────
import type { OUItem } from '@/api/types';
import type { RecapEditionDto, RecapSlideDto, RecapSlidesOuDto } from '@/api/recognition';

/** El servidor compara las OUs sin distinguir mayúsculas; aquí igual. */
const clave = (ouId: string) => ouId.trim().toLowerCase();

/** La OU y sus ancestras, de la hoja a la raíz, según el catálogo. A prueba de ciclos. */
export function cadenaDeOus(ouId: string, ous: OUItem[]): string[] {
  const porId = new Map(ous.map((ou) => [clave(ou.id), ou]));
  const cadena: string[] = [];
  const vistas = new Set<string>();
  let actual: string | null | undefined = ouId;
  while (actual && !vistas.has(clave(actual))) {
    vistas.add(clave(actual));
    cadena.push(actual);
    actual = porId.get(clave(actual))?.parentOuId;
  }
  return cadena;
}

function personalizacionDe(edicion: RecapEditionDto, ouId: string): RecapSlidesOuDto | undefined {
  return (edicion.slidesPorOu ?? []).find((p) => clave(p.ouId) === clave(ouId));
}

export interface ConfiguracionVigente {
  /** OU dueña de la personalización que aplica, o `null` si rige Global. */
  origen: string | null;
  /** Claves de los slides encendidos. */
  claves: Set<string>;
}

/**
 * Qué slides ve `ouId` (o Global, con `null`) y de quién viene esa configuración: su propia
 * personalización, la de la ancestra más cercana que tenga una, o Global.
 */
export function configuracionVigente(
  edicion: RecapEditionDto, ouId: string | null, ous: OUItem[],
): ConfiguracionVigente {
  if (ouId) {
    for (const eslabon of cadenaDeOus(ouId, ous)) {
      const propia = personalizacionDe(edicion, eslabon);
      if (propia) return { origen: propia.ouId, claves: new Set(propia.slidesActivos) };
    }
  }
  return { origen: null, claves: new Set(edicion.slides.filter((s) => s.activo).map((s) => s.clave)) };
}

/** Slides que recorrerá esa OU (o Global), en el orden de la edición. Para la vista previa. */
export function slidesParaVista(edicion: RecapEditionDto, ouId: string | null, ous: OUItem[]): RecapSlideDto[] {
  const { claves } = configuracionVigente(edicion, ouId, ous);
  return edicion.slides.filter((s) => claves.has(s.clave)).sort((a, b) => a.indice - b.indice);
}

/** Un cambio de la tabla de slides: qué slide, en qué nivel (Global = `null`) y a qué estado. */
export interface CambioDeSlide {
  ouId: string | null;
  claveSlide: string;
  isEncendido: boolean;
}

/**
 * Enciende o apaga un slide. Con `ouId === null` cambia la configuración Global (`activo` del slide).
 * Con una OU, cambia SOLO su personalización; si todavía no tenía, nace de lo que hoy hereda (de su
 * ancestra o de Global), para que tocar un slide no altere en silencio los demás.
 */
export function cambiarSlide(edicion: RecapEditionDto, cambio: CambioDeSlide, ous: OUItem[]): RecapEditionDto {
  const { ouId, claveSlide, isEncendido } = cambio;
  if (!ouId) {
    return { ...edicion, slides: edicion.slides.map((s) => (s.clave === claveSlide ? { ...s, activo: isEncendido } : s)) };
  }
  const base = personalizacionDe(edicion, ouId)?.slidesActivos ?? [...configuracionVigente(edicion, ouId, ous).claves];
  const siguiente = new Set(base);
  if (isEncendido) siguiente.add(claveSlide);
  else siguiente.delete(claveSlide);
  // En el orden de la edición, para que el documento guardado sea estable.
  const slidesActivos = edicion.slides.map((s) => s.clave).filter((c) => siguiente.has(c));
  const otras = (edicion.slidesPorOu ?? []).filter((p) => clave(p.ouId) !== clave(ouId));
  return { ...edicion, slidesPorOu: [...otras, { ouId, slidesActivos }] };
}

/** Quita la personalización de la OU: vuelve a heredar de su ancestra o de Global. */
export function quitarPersonalizacion(edicion: RecapEditionDto, ouId: string): RecapEditionDto {
  return { ...edicion, slidesPorOu: (edicion.slidesPorOu ?? []).filter((p) => clave(p.ouId) !== clave(ouId)) };
}

/** Claves encendidas en alguna personalización: sus títulos también deben estar completos. */
export function clavesEncendidasEnAlgunaOu(edicion: RecapEditionDto): Set<string> {
  return new Set((edicion.slidesPorOu ?? []).flatMap((p) => p.slidesActivos));
}

/**
 * QA 2026-10-28 — el servidor rechaza un choque de configuraciones con los ids de las OUs («La OU
 * 010ea647-… ya está en el Wrapped activo «QA Recap Europa»…»): SurveyService guarda el alcance como
 * ids y no tiene el catálogo de nombres. La pantalla sí lo tiene, así que traduce aquí cada id del
 * catálogo a su nombre. Lo que va entre «» (el nombre de la otra configuración) no se toca, y solo se
 * cambian ids completos, nunca un pedazo de otra palabra.
 */
export function conNombresDeOu(mensaje: string, ous: OUItem[]): string {
  const nombres = new Map(ous.map((ou) => [clave(ou.id), ou.name]));
  const fuera = (texto: string) => texto.replace(/[\w-]+/g, (token) => nombres.get(clave(token)) ?? token);
  return mensaje.split(/(«[^»]*»)/).map((parte) => (parte.startsWith('«') ? parte : fuera(parte))).join('');
}
